
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import { TCase_Table } from "@/models/cases_expert_cases";
import { API_ROUTES } from "@/utils/API";

import Tabs from "@/components/Tabs/Tabs.vue";
import EmployerServicesTableCA from "@/modules/Services/Components/Table/EmployerServicesTables/EmployerServicesTableCA.vue";
import EmployerServicesTableSL from "@/modules/Services/Components/Table/EmployerServicesTables/EmployerServicesTableSL.vue";
import { TTab } from "@/models/tabs";
import { convertCasesForTable, TTableFilter } from "@/models/cases_employer";
import { SMUser } from "@/store";
import {
  CANDIDATE_ASSESSMENT_SERVICE_ID,
  COS_PLUS_SKILLED_WORKER_SERVICE_ID,
  COS_PLUS_SKILLED_WORKER_INSIDE_SERVICE_ID,
  SKILLED_WORKER_SERVICE_ID,
  SPONSOR_LICENCE_SERVICE_ID,
  SKILLED_WORKER_INSIDE_SERVICE_ID,
} from "@/constants/services";

const ICONS = {
  search: "search",
};

const CANDIDATE_APPLICATION_TABLE_KEY = "candidate_application";
const SPONSOR_LICENSE_TABLE_KEY = "sponsor_license";

const CANDIDATE_APPLICATION_TAB_IDS = [
  CANDIDATE_ASSESSMENT_SERVICE_ID,
  SKILLED_WORKER_SERVICE_ID,
  COS_PLUS_SKILLED_WORKER_SERVICE_ID,
  COS_PLUS_SKILLED_WORKER_INSIDE_SERVICE_ID,
  SKILLED_WORKER_INSIDE_SERVICE_ID,
];

const LINKS = [
  {
    key: CANDIDATE_APPLICATION_TABLE_KEY,
    id: CANDIDATE_APPLICATION_TAB_IDS.join(",,"),
    label: "Candidate application",
  },
  {
    key: SPONSOR_LICENSE_TABLE_KEY,
    id: SPONSOR_LICENCE_SERVICE_ID,
    label: "Sponsor licence",
  },
];

@Component({
  components: {
    Tabs,
    EmployerServicesTableCA,
    EmployerServicesTableSL,
    Loader: () => import("@/components/Loader/Loader.vue"),
  },
})
export default class EmployerServicesTables extends Vue {
  @Prop({ type: Array, default: () => [] }) readonly cases!: TCase_Table[];
  @Prop({ type: Boolean, default: false }) readonly showHelloMessage!: boolean;

  loading = false;

  get tabs(): TTab[] {
    return LINKS.map(({ label, key, id }) => ({
      key,
      id,
      label: label.toUpperCase(),
      isActive: this.serviceTableTarget === key,
    }));
  }

  get filteredTabs() {
    const tabs = [...this.tabs];

    if (!this.showSlTab) {
      const tabIndex = tabs.findIndex((tab) => tab.key === SPONSOR_LICENSE_TABLE_KEY);

      tabs.splice(tabIndex, 1);
    }

    return tabs;
  }

  serviceTableTarget = LINKS[0].key;

  selectServiceTarget(item: TTab) {
    this.serviceTableTarget = item.key ?? "";
  }

  get isEmployerServiceTable() {
    return this.serviceTableTarget === CANDIDATE_APPLICATION_TABLE_KEY;
  }

  get isEmployeeServiceTable() {
    return this.serviceTableTarget === SPONSOR_LICENSE_TABLE_KEY;
  }

  items = [];

  get showSlTab() {
    return !(SMUser.isSponsor && !SMUser.user?.is_sl_started);
  }

  get casesCounter() {
    return this.items?.length ?? "0";
  }

  get counterLabel() {
    const count = this.casesCounter;

    if (count === 1) return this.$tc("header.counter", 1, { count });

    return this.$tc("header.counter", 2, { count });
  }

  query = "";

  get searchBarProps() {
    return {
      iconPrepend: ICONS.search,
      placeholder: this.$tc("searchbar.placeholder"),
    };
  }

  get currentServiceTargetCaseTemplateId() {
    const target = this.filteredTabs.find((tab) => tab.key === this.serviceTableTarget);

    return target.id;
  }

  filters = "";

  async fetchItems(search?: string): Promise<void> {
    try {
      this.loading = true;

      const params = {
        case_templates: this.currentServiceTargetCaseTemplateId,
      };

      if (search) Object.assign(params, { search });

      const { data } = await this.axios.get(API_ROUTES.CASES(params));

      if (!data?.cases) return;

      this.items = data.cases.map((a) => convertCasesForTable(a));

      this.filters = data.filters;
    } finally {
      this.loading = false;
    }
  }

  async mounted() {
    await this.fetchItems();
  }

  updateQuery(query: string): void {
    // if (query.length < 3 && query.length > 0) return;

    this.fetchItems(query);
  }

  @Watch("query")
  queryChanged(query: string) {
    this.updateQuery(query);
  }

  @Watch("serviceTableTarget")
  serviceTableTargetChanged(value: string) {
    if (!value) return;

    this.fetchItems(this.query);
  }

  params = {
    case_templates: this.currentServiceTargetCaseTemplateId,
  };

  onFilter({ filters, filtersName }: { filters: TTableFilter[]; filtersName: string }) {
    this.params = {
      ...this.params,
      [filtersName]: filters.map((filter) => encodeURIComponent(filter.label)).join(",,"),
    };

    const paramsLength = Object.values(this.params).filter((param) => param).length - 1;

    const params = paramsLength ? this.params : { clear: "" };

    this.axios.get(API_ROUTES.CASES(params));
  }
}
